import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { AuthStatus, authActions } from 'feature/auth/redux/authSlice'
import { getBaseQueryAuth } from 'helpers/rtkQueryHelpers'

import {
  TEditEmailRequest,
  EditProfileBody,
  EditProfileConfirmBody,
  EditProfileConfirmResult,
  TConfirmEmailResponse,
  TEditEmailResponseBody,
  User,
  UserEditResult,
  UserNotice,
  UserMainPage,
} from '../types'

export const userApi = createApi({
  reducerPath: 'user',
  baseQuery: getBaseQueryAuth,
  tagTypes: ['profile', 'mainPage', 'email'],
  refetchOnMountOrArgChange: true,
  endpoints: builder => ({
    getMainPageData: builder.query<UserMainPage, void>({
      query: () => ({
        url: '/v1/customer/profile/main',
        method: 'GET',
      }),
      providesTags: ['mainPage'],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled
          // Проверяем что пользователь всё ещё авторизован (запрос мог завершиться после логаута)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { auth } = getState() as any
          if (auth.authInfo.status !== AuthStatus.registered) return
          dispatch(
            authActions.setHasUnreadNotifications(data.hasUnreadNotifications),
          )
          if (data.clientType) {
            dispatch(authActions.setClientTypeInfo(data.clientType))
          }
        } catch (_e) {
          /* query errors handled by RTK Query */
        }
      },
    }),
    getUser: builder.query<User, void>({
      query: () => ({
        url: '/v1/customer/profile',
        method: 'GET',
      }),
      providesTags: ['profile'],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { auth } = getState() as any
          if (auth.authInfo.status !== AuthStatus.registered) return
          if (data.clientType) {
            dispatch(authActions.setClientTypeInfo(data.clientType))
          }
        } catch (_e) {
          /* query errors handled by RTK Query */
        }
      },
    }),
    editProfile: builder.mutation<UserEditResult, EditProfileBody>({
      query: body => ({
        url: '/v1/customer/change-personal-data',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['profile'],
    }),
    editUserDataConfirm: builder.mutation<
      EditProfileConfirmResult,
      EditProfileConfirmBody
    >({
      query: body => ({
        url: '/v1/customer/change-personal-data-confirm',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['profile', 'mainPage'],
    }),
    editEmail: builder.mutation<UserEditResult, TEditEmailRequest>({
      query: body => ({
        url: '/v1/customer/change-email-address',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['profile'],
    }),
    getUserNoticeData: builder.query<UserNotice, void>({
      query: () => ({
        url: '/v1/customer/settings/notice',
        method: 'GET',
      }),
      providesTags: ['profile'],
      // async onQueryStarted(_, { dispatch, queryFulfilled }) {
      //   const { data } = await queryFulfilled
      //   dispatch(authSlice.actions.setUserAuthenticated(Boolean(data)))
      // },
    }),
    editUserNoticeData: builder.mutation<UserNotice, UserNotice>({
      query: body => ({
        url: '/v1/customer/settings/notice',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['profile'],
    }),
    editUserEmailData: builder.mutation<
      TEditEmailResponseBody,
      TEditEmailRequest
    >({
      query: body => ({
        url: '/v1/customer/change-email-address',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['profile'],
    }),
    reSendEditEmail: builder.mutation<TEditEmailResponseBody, void>({
      query: () => ({
        url: '/v1/customer/re-sending-confirmation-email',
        method: 'GET',
      }),
      invalidatesTags: ['profile'],
    }),
    confirmUserEmail: builder.mutation<
      TConfirmEmailResponse,
      string | undefined
    >({
      query: token => ({
        url: `/v1/customer/change-email-confirm/${token}`,
        method: 'GET',
      }),
      invalidatesTags: ['profile'],
    }),
  }),
})

export const {
  useGetUserQuery,
  useGetUserNoticeDataQuery,
  useEditProfileMutation,
  useEditEmailMutation,
  useGetMainPageDataQuery,
  useEditUserNoticeDataMutation,
  useEditUserDataConfirmMutation,
  useEditUserEmailDataMutation,
  useReSendEditEmailMutation,
  useConfirmUserEmailMutation,
} = userApi
